import React, { useState, useCallback, createRef } from "react"
import ContentEditable from "react-contenteditable"
import { debounce } from "ts-debounce"
import backend from "~/backend"
import useDidUpdateEffect from "~/hooks/useDidUpdateEffect"
import Calendar from "react-calendar"
import strftime from "strftime"
import "react-calendar/dist/Calendar.css"

export type Props = {
  dayId: number
  tasks: TaskProps[]
  dayDate: string
  title?: string
}

type TaskProps = {
  index: number
  task: string
  completed: boolean
  level: number
  due: string | null
}

type TaskState = TaskProps & {
  ref: React.RefObject<HTMLElement>
}

const sortTasksOnLoad = (tasks: TaskProps[]) => {
  return [...tasks]
    .sort((a, b) => {
      const date1 = a.due ? Date.parse(a.due) : Number.MAX_SAFE_INTEGER
      const date2 = b.due ? Date.parse(b.due) : Number.MAX_SAFE_INTEGER

      if (date1 === date2) {
        return a.index - b.index
      }

      return date1 - date2
    })
    .map((task, idx) => ({
      ...task,
      index: idx + 1
    }))
}

const GenericTasks = ({
  dayId,
  tasks: initialTasks,
  dayDate,
  title = "Generic tasks"
}: Props) => {
  const [tasks, setTasks] = useState<TaskState[]>(() =>
    sortTasksOnLoad(initialTasks).map((task, idx) => ({
      ...task,
      index: idx + 1,
      ref: createRef<HTMLElement>()
    }))
  )
  const [focusedTask, setFocusedTask] = useState<number | null>(null)
  const [backspacePressed, setBackspacePressed] = useState<{
    index: number
    event: React.KeyboardEvent<HTMLDivElement>
  } | null>(null)

  const parsedDayDate = Date.parse(dayDate)
  const dueCount = tasks.reduce((count, task) => {
    if (!task.due || task.completed) return count

    const dueTimestamp = Date.parse(task.due)

    if (Number.isNaN(dueTimestamp) || Number.isNaN(parsedDayDate)) {
      return count
    }

    return dueTimestamp <= parsedDayDate ? count + 1 : count
  }, 0)

  const updateTask = (
    index: number,
    attributes: Partial<Pick<TaskState, "task" | "completed" | "due" | "level">>
  ) => {
    setTasks((previous) => {
      const position = index - 1
      if (position < 0 || position >= previous.length) return previous

      const next = [...previous]
      next[position] = { ...next[position], ...attributes }
      return next
    })
  }

  const toggleTask = (index: number) => {
    setTasks((previous) => {
      const position = index - 1
      if (position < 0 || position >= previous.length) return previous

      const next = [...previous]

      next[position] = {
        ...next[position],
        completed: !next[position].completed
      }

      return next
    })
  }

  const changeLevel = (index: number, direction: number) => {
    setTasks((previous) => {
      const position = index - 1
      if (position <= 0 && direction === -1) return previous
      if (position < 0 || position >= previous.length) return previous

      const task = previous[position]
      const currentLevel = task.level || 0
      let newLevel = currentLevel

      if (index !== 1 && currentLevel > 0 && direction == -1) {
        newLevel = currentLevel - 1
      } else if (index !== 1 && currentLevel < 5 && direction == 1) {
        newLevel = currentLevel + 1
      }

      const next = [...previous]
      next[position] = { ...task, level: newLevel }
      return next
    })
  }

  const persistChanges = (payload) => {
    backend.updateDay(dayId, { generic_tasks: payload })
  }

  const debouncedPersist = useCallback(debounce(persistChanges, 300), [])

  const insertAt = (index: number, after: number) => {
    const newRef = createRef<HTMLElement>()

    setTasks((previous) => {
      const insertPosition =
        after === 0 ? Math.max(0, index - 1) : Math.min(previous.length, index)

      let newLevel = 0

      if (previous.length > 0) {
        if (after === 0 && index > 1) {
          const sibling =
            previous[Math.min(insertPosition, previous.length - 1)]
          if (sibling) newLevel = sibling.level || 0
        } else if (after === 1) {
          const sibling = previous[index - 1]
          if (sibling) newLevel = sibling.level || 0
        }
      }

      const next = [...previous]
      next.splice(insertPosition, 0, {
        index: 0,
        task: "",
        completed: false,
        level: newLevel,
        due: null,
        ref: newRef
      })

      const reindexed = next.map((task, idx) => ({
        ...task,
        index: idx + 1
      }))

      setFocusedTask(insertPosition + 1)

      return reindexed
    })
  }

  const deleteTask = (index: number) => {
    if (index == 1) return

    setTasks((previous) => {
      if (index < 1 || index > previous.length) return previous

      const next = previous.filter((_, idx) => idx !== index - 1)

      const reindexed = next.map((task, idx) => ({
        ...task,
        index: idx + 1
      }))

      return reindexed
    })

    setFocusedTask(Math.max(1, index - 1))
  }

  const handleKeyUp = (
    index: number,
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    const usingCommand = event.metaKey || event.ctrlKey
    const key = event.key?.toLowerCase()

    if (usingCommand && (event.key === "Enter" || key === "d")) {
      event.preventDefault()
      toggleTask(index)
    } else if (event.shiftKey && event.key == "Enter") {
      event.preventDefault()
      insertAt(index, 0)
    } else if (event.key == "Enter") {
      event.preventDefault()
      insertAt(index, 1)
    } else if (usingCommand && key === "j") {
      event.preventDefault()
      jump({ index, direction: 1 })
    } else if (usingCommand && key === "k") {
      event.preventDefault()
      jump({ index, direction: -1 })
    } else if (event.key == "Backspace") {
      event.persist()
      setBackspacePressed({ index, event })
    } else if (!event.shiftKey && event.key == "Tab") {
      event.preventDefault()
      changeLevel(index, 1)
    } else if (event.shiftKey && event.key == "Tab") {
      event.preventDefault()
      changeLevel(index, -1)
    }
  }

  const focus = (index: number): boolean => {
    const task = tasks[index - 1]
    const element = task?.ref.current

    if (!element) return false

    element.focus()

    return true
  }

  const handleBackspace = ({
    index,
    event
  }: {
    index: number
    event: React.KeyboardEvent<HTMLDivElement>
  }) => {
    const currentTask = tasks[index - 1]

    if (!currentTask || !currentTask.ref.current) return

    if (currentTask.ref.current.textContent?.length == 0) {
      event.preventDefault()
      deleteTask(index)
    }
  }

  const jump = ({ index, direction }: { index: number; direction: number }) => {
    const target = index + direction

    if (target < 1 || target > tasks.length) return

    const ref = tasks[target - 1].ref.current

    ref.focus()

    if (ref.firstChild) {
      let range = new Range()
      range.setStart(ref.firstChild, ref.firstChild.length)
      let selection = window.getSelection()!
      selection.removeAllRanges()
      selection.addRange(range)
    }
  }

  useDidUpdateEffect(() => {
    if (focusedTask == null) return

    if (typeof window === "undefined") {
      setFocusedTask(null)
      return
    }

    let frame: number | null = null
    let attempts = 0
    const maxAttempts = 5

    const tryFocus = () => {
      attempts += 1
      const isFocused = focus(focusedTask)

      if (isFocused) {
        setFocusedTask(null)
        return
      }

      if (attempts >= maxAttempts) {
        setFocusedTask(null)
        return
      }

      frame = window.requestAnimationFrame(tryFocus)
    }

    tryFocus()

    return () => {
      if (frame !== null) {
        window.cancelAnimationFrame(frame)
      }
    }
  }, [focusedTask, tasks])

  useDidUpdateEffect(() => {
    const payload = tasks.map((el) => ({
      index: el.index,
      task: el.task,
      completed: el.completed,
      due: el.due,
      level: el.level || 0
    }))

    debouncedPersist(payload)
  }, [tasks])

  useDidUpdateEffect(() => {
    if (backspacePressed == null) return

    handleBackspace(backspacePressed)

    setBackspacePressed(null)
  }, [backspacePressed])

  // These need to be written down (and not dynamic) because tailwind needs to generate them on the
  // fly.
  // If they are dynamic, the tailwind JIT compiler won't be able to find them.
  const indents = {
    0: "",
    1: "ml-[30px]",
    2: "ml-[60px]",
    3: "ml-[90px]",
    4: "ml-[120px]",
    5: "ml-[150px]"
  }

  const calculateIndent = (task) => {
    if (!task.level) return ""

    return indents[task.level]
  }

  const dayLabel = strftime("%A", new Date(Date.parse(dayDate)))
  const heading = title ?? dayLabel

  return (
    <div>
      <div className="mb-2 pb-2 pr-3 border-b border-fg-muted border-dashed flex items-center justify-between gap-2">
        <div className="py-0 md:py-2 text-2xl text-fg font-heading">
          {heading}
        </div>
        {dueCount > 0 && (
          <span
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-danger/10 text-danger text-sm font-semibold"
            title="Overdue tasks"
          >
            <span className="uppercase tracking-wide text-[10px]">Overdue</span>
            <span className="text-lg leading-none">{dueCount}</span>
          </span>
        )}
      </div>
      <div className="w-full pl-4 mt-4 border-l-4 border-accent space-y-1 pr-4">
        {tasks.map((task) => (
          <div
            key={task.index}
            className={`flex space-x-2 ${calculateIndent(task)}`}
          >
            <div>
              <input
                type="checkbox"
                id={`generic-tasks-${task.index}`}
                className="cursor-pointer checkbox-input"
                onChange={() => {
                  toggleTask(task.index)
                }}
                checked={task.completed}
              />
              <label htmlFor={`generic-tasks-${task.index}`}>
                <span className="cursor-pointer checkbox checkbox-accent"></span>
              </label>
            </div>
            <ContentEditable
              innerRef={task.ref}
              html={task.task || ""}
              onKeyDown={(event) => handleKeyUp(task.index, event)}
              onChange={(event) => {
                updateTask(task.index, { task: event.target.value })
              }}
              className={`flex-1 focus:outline-none ${
                task.completed ? "checkbox-checked" : ""
              }`}
            />
            <DueDate
              initialDate={task.due}
              dayDate={dayDate}
              onUpdate={(date) => updateTask(task.index, { due: date })}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

const DueDate = ({ initialDate, onUpdate, dayDate }) => {
  const date = initialDate ? new Date(Date.parse(initialDate)) : null
  const [opened, setOpened] = useState(false)

  const today = new Date()
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)

  const changeDate = (newDate) => {
    onUpdate(strftime("%Y-%m-%d", newDate))
    setOpened(false)
  }

  const getColors = () => {
    if (date) {
      const dayDateDate = new Date(Date.parse(dayDate))
      const daysLeft =
        (date.getTime() - dayDateDate.getTime()) / (1000 * 3600 * 24)

      if (daysLeft <= 0) {
        return "text-danger border-danger font-bold"
      } else if (daysLeft <= 1) {
        return "text-accent border-accent"
      } else if (daysLeft <= 3) {
        return "text-warning border-warning"
      } else if (daysLeft <= 7) {
        return "text-warning border-warning"
      } else {
        return "text-fg border-fg-muted"
      }
    } else {
      return "text-fg border-fg-muted"
    }
  }

  return (
    <div className="relative">
      <span
        className={`${getColors()} border-b  border-dashed font-heading b-8 cursor-pointer`}
        onClick={() => {
          setOpened((prev) => !prev)
        }}
      >
        {date && strftime("%b %d", date)}
        {!date && <CalendarIcon />}
      </span>
      <div
        className={`bg-white z-20 mt-2 ${
          opened ? "absolute right-0 shadow-md rounded-md" : "hidden"
        }`}
      >
        <Calendar
          onChange={changeDate}
          value={date}
          className="rounded-md px-4 py-2"
          minDate={tomorrow}
        />
      </div>
    </div>
  )
}

const CalendarIcon = () =>
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-4">
    <path d="M7 11C7 10.4477 7.44772 10 8 10C8.55228 10 9 10.4477 9 11C9 11.5523 8.55228 12 8 12C7.44772 12 7 11.5523 7 11Z" fill="#000000"/>
    <path d="M7 16C7 15.4477 7.44772 15 8 15C8.55228 15 9 15.4477 9 16C9 16.5523 8.55228 17 8 17C7.44772 17 7 16.5523 7 16Z" fill="#000000"/>
    <path d="M11 11C11 10.4477 11.4477 10 12 10C12.5523 10 13 10.4477 13 11C13 11.5523 12.5523 12 12 12C11.4477 12 11 11.5523 11 11Z" fill="#000000"/>
    <path d="M11 16C11 15.4477 11.4477 15 12 15C12.5523 15 13 15.4477 13 16C13 16.5523 12.5523 17 12 17C11.4477 17 11 16.5523 11 16Z" fill="#000000"/>
    <path d="M15 11C15 10.4477 15.4477 10 16 10C16.5523 10 17 10.4477 17 11C17 11.5523 16.5523 12 16 12C15.4477 12 15 11.5523 15 11Z" fill="#000000"/>
    <path d="M15 16C15 15.4477 15.4477 15 16 15C16.5523 15 17 15.4477 17 16C17 16.5523 16.5523 17 16 17C15.4477 17 15 16.5523 15 16Z" fill="#000000"/>
    <path fillRule="evenodd" clipRule="evenodd" d="M8 2C8.55228 2 9 2.44772 9 3V4H15V3C15 2.44772 15.4477 2 16 2C16.5523 2 17 2.44772 17 3V4C19.7614 4 22 6.23858 22 9V17C22 19.7614 19.7614 22 17 22H7C4.23858 22 2 19.7614 2 17V9C2 6.23858 4.23858 4 7 4V3C7 2.44772 7.44772 2 8 2ZM7 6C5.34315 6 4 7.34315 4 9V17C4 18.6569 5.34315 20 7 20H17C18.6569 20 20 18.6569 20 17V9C20 7.34315 18.6569 6 17 6C17 6.55229 16.5523 7 16 7C15.4477 7 15 6.55229 15 6H9C9 6.55229 8.55228 7 8 7C7.44772 7 7 6.55229 7 6Z" fill="#000000"/>
  </svg>

export default GenericTasks
